import { inject, Injectable, signal } from '@angular/core';
import { CookieService as NgxCookieService } from 'ngx-cookie-service';
import { first } from 'rxjs/operators';
import {
  CONNEXION_EN_TANT_QUE_COOKIE_NAME,
  CONNEXION_TYPE_COOKIE_NAME,
  ENVIRONMENT_CONFIG,
} from '@movinmotion/conf-shared';
import { tap } from 'rxjs';
import { CacheService } from '../cache/cache.service';
import { MultiTabService } from '@movinmotion/util-ngx';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private readonly env = inject(ENVIRONMENT_CONFIG);
  private readonly movinmotionBackendUrl = this.env.api.movinmotionBackend;

  private readonly SESSION_COOKIE_NAME = 'M_SESSION';

  private readonly ngxCookieService = inject(NgxCookieService);
  private readonly http = inject(HttpClient);
  private readonly cache = inject(CacheService);
  private readonly multiTab = inject(MultiTabService);

  getCompanyId(): string | null {
    return this.getCookieInfo('companyId');
  }

  getUserId(): string | null {
    return this.getCookieInfo('loggedUserId');
  }

  getCookieInfo(param: string): string | null {
    let res: string | null = this.ngxCookieService.get(this.SESSION_COOKIE_NAME);
    if (res) {
      try {
        res = res.split(`${param}=`)[1].split('&')[0];
      } catch (e) {
        res = null;
      }
    }
    return res;
  }

  hasSessionCookie() {
    return this.ngxCookieService.get(this.SESSION_COOKIE_NAME);
  }

  /**
   * Identifiant du compte sur lequel une connexion en tant que est en cours, null sinon.
   */
  private readonly _cibleConnexionEnTantQue = signal<string | null>(this.lireCibleConnexionEnTantQue());
  readonly cibleConnexionEnTantQue = this._cibleConnexionEnTantQue.asReadonly();

  /**
   * Vrai si une connexion "en tant que" était déjà en cours au chargement de cet onglet, donc si
   * l'écran affiché est celui de la cible.
   */
  readonly ongletOuvertDansConnexionEnTantQue = this._cibleConnexionEnTantQue() !== null;

  /**
   * Permet de rafraîchir la cible de la connexion "en tant que" après un changement de session.
   */
  rafraichirCibleConnexionEnTantQue(): void {
    this._cibleConnexionEnTantQue.set(this.lireCibleConnexionEnTantQue());
  }

  private lireCibleConnexionEnTantQue(): string | null {
    return this.ngxCookieService.get(CONNEXION_EN_TANT_QUE_COOKIE_NAME) || null;
  }

  setCookieSessionByCompanyId(companyId: string): void {
    this.http
      .post(`${this.movinmotionBackendUrl}/serve/update-cookie-company?companyId=${companyId}`, {})
      .pipe(
        tap(() => this.onCompanyChange(companyId)),
        first(),
      )
      .subscribe(() => location.reload());
  }

  suppressAuthCookie() {
    this.ngxCookieService.delete(this.SESSION_COOKIE_NAME, '/', this.env.cookieDomain.replace('.app', ''));
  }

  onCompanyChange(companyId: string): void {
    this.cache.companyChanged$.next(true);
    this.multiTab.onCompanyChange(companyId);
  }

  isAProvider() {
    const cookieValue = this.ngxCookieService.get(CONNEXION_TYPE_COOKIE_NAME);
    return JSON.parse(cookieValue && cookieValue.length ? cookieValue : '{}')?.connectionMode === 'PROVIDER';
  }
}
